import type { Partner } from "@expert/shared-types";

import type { EligibleProduct } from "../../../../types";

import { SaleProgress } from "../../../../types";

// VZN_VMPMD, VZN_VMP_MD, and VZN_VMP_SD are distinct Verizon products, not SKUs for one offering —
// all follow identical show/no-show rules, but if a customer is eligible for more than one, all
// should show. VZN_VMP_SD is the planned replacement for VZN_VMPMD; it's added here ahead of the
// Contentful cutover so it renders correctly once it starts coming back from eligibility.
const VMP_MD_SKUS = ["VZN_VMPMD", "VZN_VMP_MD", "VZN_VMP_SD"];

// AT&T plus its channel variants. They sell the same offer set, so anything keyed on "att" alone
// silently diverges for a variant — the reason `attnac` was rendering the two static Protect
// Advantage SKUs as standalone tiles and labelling itself "Attnac".
const ATT_FAMILY_PARTNERS: string[] = ["att", "attnac"];

export function isAttFamilyPartner(partner: string) {
    return ATT_FAMILY_PARTNERS.includes(partner);
}

// Additional-SKU options on the AT&T sale flow, not offers in their own right.
const ATT_ADDITIONAL_SKU_ONLY = ["ATT_PA1_STATIC", "ATT_PA4_STATIC"];

export function shouldShowVerizonProduct(params: {
    product: EligibleProduct;
    products: EligibleProduct[];
    client: string;
    isVmpSalesEnabled: boolean;
}) {
    const { product, products, client, isVmpSalesEnabled } = params;

    if (!VMP_MD_SKUS.includes(product.productSku)) {
        return true;
    }

    if (client === "VERIZON_IBC" || client === "VERIZON_OFR" || isVmpSalesEnabled) {
        return true;
    }

    // Known gap: VMP shows after any AAL sale with no protected-state check, here or in the API, so a
    // protected-state customer with a non-domestic expert can see VMP. Whether to close it is a Product
    // decision, tracked in expert-workspace#7898.
    return products.find((p) => p.productSku === "VZN_AAL")?.saleProgress === SaleProgress.Success;
}

export function shouldRenderProduct(params: {
    product: EligibleProduct;
    partner: string;
    products: EligibleProduct[];
    client: string;
    isVmpSalesEnabled: boolean;
}) {
    const { product, partner } = params;

    if (isAttFamilyPartner(partner)) {
        return !ATT_ADDITIONAL_SKU_ONLY.includes(product.productSku);
    }

    return shouldShowVerizonProduct(params);
}

export function getVerizonCareProductInfo(params: {
    partner: string;
    pilots: string[];
    isVerizonCareEnabled: boolean;
}) {
    const { partner, pilots, isVerizonCareEnabled } = params;

    const isVerizonCareUser = pilots.includes("care") && isVerizonCareEnabled;
    return isVerizonCareUser ? "verizonCare" : partner;
}

export function formatIneligibleProductDisplay(params: { product: EligibleProduct; partner: string }) {
    const { product, partner } = params;

    const displayName = product.productInfo?.["en-US"]?.displayName ?? product.productSku;
    return formatPartnerProductDisplay(partner, displayName);
}

// Some product display names carry the partner brand themselves — HOMEPRO's is becoming
// "AT&T HomeTech Protection" so that AT&T's canonical product name reaches everything that renders
// the display name, the FTC consent comm included. Prepending the brand on top of a name that
// already has it renders "AT&T AT&T HomeTech Protection", so strip a leading brand before
// composing. Checks the partner key, its display brand, and AT&T's legacy "ATT" spelling.
export function stripLeadingPartnerBrand(name: string, partner: Partner) {
    const candidates = [partner, mapPartnerToDisplayName(partner), ...(isAttFamilyPartner(partner) ? ["ATT"] : [])];

    for (const candidate of candidates) {
        if (!name.toLowerCase().startsWith(candidate.toLowerCase())) continue;

        // Only strip when the brand is a whole leading token — the next character must be whitespace,
        // or there is nothing after it. Both fallback paths here pass a raw productSku when Contentful
        // has no displayName (CustomerView's enrolled line, and formatIneligibleProductDisplay), and
        // AT&T's SKUs are `ATT_`-prefixed: a bare startsWith would shave "ATT" off "ATT_MI_FIBER" and
        // render "AT&T _MI_FIBER". Leaving such a SKU intact keeps the pre-existing "ATT ATT_MI_FIBER"
        // shape rather than inventing a mangled one.
        const rest = name.slice(candidate.length);
        if (rest === "" || /^\s/.test(rest)) return rest.trimStart();
    }

    return name;
}

export function formatPartnerProductDisplay(partner: Partner, productName: string) {
    const brand = mapPartnerToDisplayName(partner);
    const withoutBrand = stripLeadingPartnerBrand(productName, partner);

    return withoutBrand ? `${brand} ${withoutBrand}` : brand;
}

export function mapPartnerToDisplayName(partner: Partner) {
    switch (partner) {
        case "adt":
        case "adtbdpp":
            return "ADT";
        case "att":
        case "attnac":
            // AT&T's canonical brand form, not the "ATT" shorthand.
            return "AT&T";
        case "asurionhomeplus":
            return "Asurion";
        case "fios":
            return "Verizon";
        case "amazonrouter":
            return "Amazon";
        default:
            return partner.charAt(0).toUpperCase() + partner.slice(1);
    }
}

export function getProductCardVariant({
    product,
    isChat,
}: {
    product: EligibleProduct;
    isChat: boolean;
}): "full" | "compact" {
    if (isChat) return "full";
    return product.productInfo?.["en-US"]?.cardVariant === "full" ? "full" : "compact";
}

export type ProductCategory = {
    primary: EligibleProduct[];
    secondary: EligibleProduct[];
    ineligible: EligibleProduct[];
};

export function categorizeProductsByType(params: {
    products: EligibleProduct[];
    partner: string;
    client: string;
    isVmpSalesEnabled: boolean;
}): ProductCategory {
    const { products, partner, client, isVmpSalesEnabled } = params;
    const eligibleProducts = products.filter((product) => product.status === "ELIGIBLE" && !!product.productInfo);
    const ineligibleProducts = products.filter((product) => product.status !== "ELIGIBLE" && !!product);

    const primary: EligibleProduct[] = [];
    const secondary: EligibleProduct[] = [];
    const ineligible: EligibleProduct[] = [];

    eligibleProducts.forEach((product) => {
        const override = isRecommendedOverride(products, client, product.productSku);

        if (override !== undefined) {
            if (override) {
                const overriddenProduct = {
                    ...product,
                    productInfo: {
                        ...product.productInfo,
                        "en-US": {
                            ...product.productInfo?.["en-US"],
                            cardVariant: "full",
                            isRecommended: true,
                        },
                    },
                };
                primary.push(overriddenProduct as EligibleProduct);
            } else {
                const overriddenProduct = {
                    ...product,
                    productInfo: {
                        ...product.productInfo,
                        "en-US": {
                            ...product.productInfo?.["en-US"],
                            cardVariant: "compact",
                            isRecommended: false,
                        },
                    },
                };
                secondary.push(overriddenProduct as EligibleProduct);
            }
        } else {
            if (
                shouldRenderProduct({ product, partner, products, client, isVmpSalesEnabled }) &&
                product.productInfo?.["en-US"]?.cardVariant === "full"
            ) {
                primary.push(product);
            } else if (shouldRenderProduct({ product, partner, products, client, isVmpSalesEnabled })) {
                secondary.push(product);
            }
        }
    });

    ineligibleProducts.forEach((product) => {
        if (shouldRenderProduct({ product, partner, products, client, isVmpSalesEnabled })) {
            ineligible.push(product);
        }
    });

    return { primary, secondary, ineligible };
}

export function isRecommendedOverride(products: EligibleProduct[], client?: string, productSku?: string) {
    if (client === "ATT_IBC") {
        //for ATT_IBC calls, only in the case where the customer is a non-subscriber will we ever sort PA1 to be the first element returned from eligibility.
        const firstProduct = products.at(0);
        if (firstProduct?.productSku === "ATT_PA1_NOT_WITH_AAL") {
            return productSku === "ATT_PA1_NOT_WITH_AAL";
        }
    }
}

export interface OfferPresentedLogFields {
    productOfferId: string;
    productSku: string;
    sessionId: string;
}

export const offerPresentedKey = (sessionId: string, productOfferId: string) => `${sessionId}:${productOfferId}`;

/**
 * Returns log fields for offers in `offers` whose key is not already in `presented`.
 * Pure: does not mutate `presented` or `offers`. Intra-batch duplicates are deduped
 * (same offer id appearing twice yields one entry). The caller is responsible for
 * recording the returned keys into its own dedupe set.
 */
export function pickUnpresentedOffers(
    offers: readonly EligibleProduct[],
    presented: ReadonlySet<string>,
    sessionId: string,
): OfferPresentedLogFields[] {
    const seen = new Set<string>();
    const newlyPresented: OfferPresentedLogFields[] = [];
    for (const offer of offers) {
        const key = offerPresentedKey(sessionId, offer.id);
        if (presented.has(key) || seen.has(key)) continue;
        seen.add(key);
        newlyPresented.push({ productOfferId: offer.id, productSku: offer.productSku, sessionId });
    }
    return newlyPresented;
}
